/**
 * CRM feature routes — the CRM module's contribution to the authenticated route
 * tree (see src/features/routes-registry.ts for the contract).
 *
 * CRM owns the `/crm` subtree as a NESTED LAYOUT ROUTE. CrmLayout (via the
 * shared CrmModuleShell) provides the .crm-scope styling + MeProvider + an
 * Expense-style pill-tab bar across the top (Home / Work Queue / Customers /
 * Account Health / Segments & Lists — capability-gated); the pages render in
 * its <Outlet>. No in-module sidebar, no nested rail items.
 *
 * TWO PAGES WERE RETIRED 2026-09-08 (Taylor), and both were genuine duplicates
 * rather than cuts:
 *
 *   /crm/followups — follow-ups were producer 1 of the Work Queue, so this was
 *     a strictly narrower view of that screen. "In follow ups I don't think this
 *     is needed seems liek work queue is already doing this." The one thing it
 *     had that the queue did not was CREATE and EDIT; those moved into the
 *     queue first, against endpoints that already existed.
 *
 *   /crm/contacts — "I am not seeing the purpurse of the contacts page, seems
 *     like that is in customers and segments and lists." Its consent-aware,
 *     audited export was NOT lost with it: that logic was always
 *     api/src/lib/contact-list.ts, and the page was only ever one caller. The
 *     Segments page now calls the same library, so suppression and the export
 *     audit apply exactly as they did here.
 *
 * IA note (2026-07): Hotel Supplier Management was promoted to its OWN top-level
 * module (/hotel-supplier) — see features/hotel-supplier. Users & Roles is owned
 * by the platform Admin (/admin/users), so the old /crm/admin/users page is gone.
 * The hubspot / ae-routing pages remain routed but UNSURFACED (HubSpot is
 * backend-only; AE routing isn't needed yet) — reachable by URL, not on the rail.
 * (The Notebooks module and the platform-egress compliance page were removed in
 * 2026-07 — the CRM no longer surfaces the Python-jobs platform.)
 *
 * Next.js file-based routes → react-router (the Next→Vite port):
 *   app/page.tsx (redirect)        → index → <Navigate to="/crm/home">
 *   app/customers/[id]/page.tsx    → customers/:id (useParams, was use(params))
 *   ...the rest map 1:1.
 */
import { Navigate, Route } from 'react-router';
import type { FeatureRouteList } from '@/features/routes-registry';
import { CrmLayout } from './CrmLayout';
import HomePage from './pages/home/page';
import CustomersPage from './pages/customers/page';
import CustomerProfilePage from './pages/customers/[id]/page';
import WorkQueuePage from './pages/work-queue/page';
import AccountHealthPage from './pages/account-health/page';
import RenewalsPage from './pages/renewals/page';
import SegmentsPage from './pages/segments/page';
import AdminHubspotPage from './pages/admin/hubspot/page';
import AdminAeRoutingPage from './pages/admin/ae-routing/page';

export const crmRoutes: FeatureRouteList = [
  <Route element={<CrmLayout />} key="crm" path="crm">
    {/* Root of /crm redirects to the home dashboard (was app/page.tsx redirect). */}
    <Route
      element={<Navigate replace to="/crm/home" />}
      index
      key="crm-index"
    />
    <Route element={<HomePage />} key="crm-home" path="home" />
    <Route element={<CustomersPage />} key="crm-customers" path="customers" />
    <Route
      element={<CustomerProfilePage />}
      key="crm-customer"
      path="customers/:id"
    />
    <Route element={<WorkQueuePage />} key="crm-work-queue" path="work-queue" />
    <Route element={<RenewalsPage />} key="crm-renewals" path="renewals" />
    <Route
      element={<AccountHealthPage />}
      key="crm-account-health"
      path="account-health"
    />
    <Route element={<SegmentsPage />} key="crm-segments" path="segments" />
    {/* Unsurfaced (not on the rail) — reachable by URL only. */}
    <Route
      element={<AdminHubspotPage />}
      key="crm-admin-hubspot"
      path="admin/hubspot"
    />
    <Route
      element={<AdminAeRoutingPage />}
      key="crm-admin-ae-routing"
      path="admin/ae-routing"
    />
  </Route>,
];
